import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton, InputAdornment, MenuItem, OutlinedInput, Select, TableCell,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { FILTER_ALL } from '../constants';
import { RADIUS, tableTokens } from '../../theme/muiTheme';

export const dataTableSx = (theme) => {
  const tokens = tableTokens(theme);
  return {
    '& thead th': {
      backgroundColor: tokens.headBg,
      color: theme.palette.text.primary,
      borderBottom: 0,
    },
    '& thead tr:last-of-type th': { borderBottom: `2px solid ${tokens.headRule}` },
    '& tbody td': { py: 1.25, borderBottom: `1px solid ${tokens.rowRule}` },
    '& tbody tr:last-of-type td': { borderBottom: 0 },
  };
};

export const tableActionCellSx = {
  whiteSpace: 'nowrap',
  '& .MuiButton-root, & .MuiIconButton-root': { flexShrink: 0 },
};

/** Sticky footer row cells — keeps the Total row pinned while the table scrolls. */
export const stickyTotalCellSx = (theme) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
  bgcolor: tableTokens(theme).headBg,
  borderTop: `1px solid ${tableTokens(theme).headRule}`,
});

/** Row 1 — the column name. Title Case, not shouted. */
export function HeadLabelCell({ children, align, width }) {
  return (
    <TableCell
      align={align || 'left'}
      sx={{
        width,
        pt: 1.75,
        pb: 1,
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'none',
        letterSpacing: 0.1,
      }}
    >
      {children}
    </TableCell>
  );
}

/** Row 2 — hosts a filter control, or nothing so the band stays continuous. */
export function FilterCell({ children, align }) {
  return (
    <TableCell
      align={align || 'left'}
      sx={{ pt: 0, pb: 1.5, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}
    >
      {children}
    </TableCell>
  );
}

/** Shared look: quiet, low-contrast, clearly secondary to the row data. */
const CONTROL_SX = {
  width: '100%',
  height: 34,
  bgcolor: 'background.paper',
  fontSize: 12.5,
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 0,
  borderRadius: `${RADIUS.filter}px`,
  color: 'text.primary',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },
};

const INPUT_SX = {
  py: 0,
  fontSize: 12.5,
  '&::placeholder': { color: 'text.disabled', opacity: 1 },
};

const MENU_ITEM_SX = { fontSize: 12.5 };

export function TextColumnFilter({
  value, onChange, placeholder, ariaLabel,
}) {
  const { t } = useTranslation();
  return (
    <OutlinedInput
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder || t('common.filter')}
      inputProps={{ 'aria-label': ariaLabel, sx: INPUT_SX }}
      startAdornment={(
        <InputAdornment position="start" sx={{ mr: 0.5 }}>
          <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
        </InputAdornment>
      )}
      endAdornment={value ? (
        <InputAdornment position="end">
          <IconButton
            size="small"
            onClick={() => onChange('')}
            aria-label={t('common.clearFilter')}
          >
            <CloseIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </InputAdornment>
      ) : null}
      sx={CONTROL_SX}
    />
  );
}

export function SelectColumnFilter({
  value, onChange, options, allLabel, ariaLabel,
}) {
  const isAll = value === FILTER_ALL;

  return (
    <Select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ 'aria-label': ariaLabel }}
      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
      sx={{
        ...CONTROL_SX,
        '& .MuiSelect-select': {
          py: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: 34,
          fontSize: 12.5,
          color: isAll ? 'text.disabled' : 'text.primary',
        },
      }}
    >
      <MenuItem value={FILTER_ALL} sx={MENU_ITEM_SX}>
        {allLabel || 'All'}
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={String(option.value)}
          value={option.value}
          sx={MENU_ITEM_SX}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}